import { useProgramme } from "@components/__templates__/ProgrammeDetailsPage/ProgrammeDetailsProvider";
import ModalEMBAMergeForm from "@components/ModalEMBAMergeForm/ModalEMBAMergeForm";
import { GeneralLink } from "@customTypes/generalLink";
import { useDevice } from "@utilities/react/get-device/get-device";
import clsx from "clsx";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import React, { use, useContext, useEffect, useRef, useState } from "react";
import { useProgrammeDetailsPage } from "../../context/programmeDetailsPage";
import { UserContext } from "../../context/user";
import Arrow from "../../public/assets2019/img/svg/arrow-carousel.svg";
import styles from "./C03.1_SubNavigationDE.module.scss";
import NavCtas from "./NavCtas";
import NavDropdown from "./NavDropdown";
import NavLink from "./NavLink";
import { useApplicationLink } from "@utilities/applicationLinkHelper";
import { fetchApi } from "@utilities/fetchApi";

const C721_ProgrammeDetailsDE = dynamic(
  () =>
    import(
      "@components/C72.1_ProgrammeDetailsBarDE/C72.1_ProgrammeDetailsBarDE"
    )
);

const C722_ProgrammeDetailsBarEMbaMerge = dynamic(
  () =>
    import(
      "@components/C72.2_ProgrammeDetailsBarEMbaMerge/C72.2_ProgrammeDetailsBarEMbaMerge"
    )
);

export interface SubNavigationDEProps {
  ctaButton1: string;
  ctaButton1Text: string;
  ctaLink1: GeneralLink;
  programmeDetailsBarDe: any; // C721_ProgrammeDetailsDE or C722_ProgrammeDetailsBarEMbaMerge
  navigationLinks: (GeneralLink | any)[]; // or C03.1_SubNavigationDEDropdown
  formHeaderEmbaMerge: string;
  buttonTextEmbaMerge: string;
  errorMessageEmbaMerge: string;
}
type Props = { content: SubNavigationDEProps };

const SubNavigationDE: React.FC<Props> = ({ content }: Props) => {
  const stickyRef = useRef<HTMLDivElement>(null);
  const navigationRef = useRef<HTMLDivElement>(null);
  const stickComboRef = useRef<HTMLDivElement>(null);
  const stickComboWrapRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navOffsetTop = useRef<number | null>(null);
  const navListRef = useRef<HTMLUListElement>(null);
  const focusableElementsRef = useRef<HTMLElement[]>([]);

  const [isSticky, setIsSticky] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [navHeight, setNavHeight] = useState(0);
  const [subNavOpenId, setSubNavOpenId] = useState<string | null>(null); // track which subnav is open
  const [showLightbox, setShowLightbox] = useState(false);
  const [modalOptions, setModalOptions] = useState<any[]>([]);

  const device = useDevice();
  const { programmeTypeCode } = useProgramme();
  const router = useRouter();
  const { programmeApplyOnlineParameterValue, applicationInSalesforce } = useProgrammeDetailsPage();
  const { user } = useContext(UserContext);

  const navListId = "nav_list";

  const {
    ctaButton1,
    ctaButton1Text,
    ctaLink1,
    programmeDetailsBarDe,
    navigationLinks,
    formHeaderEmbaMerge,
    buttonTextEmbaMerge,
    errorMessageEmbaMerge,
  } = content;

  const isC722ProgrammeDetailsBarEMbaMerge =
    programmeDetailsBarDe?.component === "c722ProgrammeDetailsBarEMbaMerge";

  const enableEmba2027intakeOptions =
    programmeDetailsBarDe?.fields?.enableEmba2027intakeOptions;

  const INTAKE_YEAR = 2027;
  const JANUARY = 1;
  const SEPTEMBER = 9;

  const cohortOf = (startDateString: string) => {
    const date = new Date(startDateString);
    return {
      date,
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
    };
  };

  useEffect(() => {
    if (!enableEmba2027intakeOptions) {
      setModalOptions([]);
      return;
    }

    const getData = async () => {
      const programmeDetailPages =
        programmeDetailsBarDe?.fields?.programmeDetailPages ?? [];

      const contentfulTypeCodes = programmeDetailPages.map(
        (obj) => obj?.fields?.programmeTypeCode
      );

      if (!contentfulTypeCodes.length) return;

      let data;
      try {
        data = await fetchApi("/api/getProgrammeDetailsBarData", {
          typeCodes: contentfulTypeCodes.join(","),
        });
      } catch (error) {
        console.error("Failed to load EMBA 2027 intake options", error);
        setModalOptions([]);
        return;
      }

      // The API already scopes results to these typeCodes and to future
      // start dates, so no extra client-side type-code filter is needed.
      const importData = data?.programmeImportDataCollection ?? [];

      const options = importData.flatMap((item) => {
        const cohort = cohortOf(item.programmeStartDate);

        const isTargetIntake =
          cohort.year === INTAKE_YEAR &&
          (cohort.month === JANUARY || cohort.month === SEPTEMBER);

        if (!isTargetIntake) return [];

        const contentfulProgramme = programmeDetailPages.find(
          (obj) =>
            obj?.fields?.programmeTypeCode === item.importDataProgrammeTypeCode
        );

        const isSalesforceApplication =
          item.applicationInSalesforce !== undefined
            ? item.applicationInSalesforce
            : cohort.year === INTAKE_YEAR && cohort.month === SEPTEMBER;

        const formattedDate = cohort.date.toLocaleDateString("en-GB", {
          month: "short",
          year: "numeric",
        });

        const [location, deliveryMode] = item.programmeLocation.split(" - ");

        return [
          {
            value: item.importDataProgrammeTypeCode,
            loc: location,
            date: formattedDate,
            deliveryMode,
            applyId:
              contentfulProgramme?.fields?.programmeApplyOnlineParameterValue,
            isSalesforceApplication,
          },
        ];
      });

      setModalOptions(options);
    };

    getData();
  }, [programmeDetailsBarDe, enableEmba2027intakeOptions]);

  const regexLondon = /London/i; // Case-insensitive

  const legacyModalLocations =
    isC722ProgrammeDetailsBarEMbaMerge && !enableEmba2027intakeOptions
      ? programmeDetailsBarDe?.fields?.programmeDetailPages
        ?.map((obj) => {
          const location = regexLondon.test(
            obj?.fields?.programmeTypeCode
          )
            ? "London"
            : "Dubai";

          return {
            value: location,
            loc: location,
            date: "",
            deliveryMode: "",
            applyId: obj?.fields?.programmeApplyOnlineParameterValue,
            isSalesforceApplication: false,
          };
        })
        .filter(
          (item, index, self) =>
            self.findIndex((obj) => obj.value === item.value) === index
        ) || []
      : [];

  let programmeId = programmeTypeCode;

  if (!programmeTypeCode) {
    programmeId =
      content?.programmeDetailsBarDe?.fields?.programmeSelection?.fields
        ?.programmeTypeCode;
  }

  if (!programmeTypeCode && isC722ProgrammeDetailsBarEMbaMerge) {
    programmeId = programmeDetailsBarDe?.fields?.programmeDetailPages?.map(
      (obj) => obj["fields"].programmeTypeCode
    )[0];
  } else if (!programmeTypeCode) {
    programmeId =
      content?.programmeDetailsBarDe?.fields?.programmeSelection?.fields
        ?.programmeTypeCode;
  }

  const closeLightbox = () => {
    setTimeout(() => {
      setShowLightbox(false);
    }, 300);
  };

  let programmeApplyOnlineId = programmeApplyOnlineParameterValue;

  if (!programmeApplyOnlineParameterValue) {
    programmeApplyOnlineId =
      content?.programmeDetailsBarDe?.fields?.programmeSelection?.fields
        ?.programmeApplyOnlineParameterValue;
  }

  let isSalesforceApplication = applicationInSalesforce;
  if (!applicationInSalesforce) {
    isSalesforceApplication = content?.programmeDetailsBarDe?.fields?.programmeSelection?.fields?.applicationInSalesforce || false;
  }

  const handleSubmenuToggle = (id: string) => {
    setSubNavOpenId((prevId) => {
      return prevId === id ? null : id;
    });
  };

  const handleFirstLevelKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const { key } = event;
    if (
      ![
        "ArrowLeft",
        "ArrowRight",
        "ArrowDown",
        "ArrowUp",
        "Home",
        "End",
      ].includes(key)
    )
      return;

    // Get all first-level focusable navigation elements
    const focusableElements = focusableElementsRef.current;
    if (!focusableElements || focusableElements.length === 0) return;

    const currentIndex = focusableElements.findIndex(
      (el) => el === event.currentTarget
    );
    if (currentIndex === -1) return;

    let nextIndex: number;
    switch (key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = (currentIndex + 1) % focusableElements.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex =
          (currentIndex - 1 + focusableElements.length) %
          focusableElements.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = focusableElements.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    focusableElements[nextIndex].focus();
  };

  const scrollTo = (el, anchorId) => {
    let sectionHead: HTMLElement = null;
    try {
      sectionHead = document.querySelector(anchorId);
    } catch (e) {
      console.error("Error finding section head:", e);
    }

    const container = stickyRef.current;
    if (!sectionHead || !container) return;

    if (sectionHead) {
      const focusTimeout =
        Math.abs(sectionHead.getBoundingClientRect().top / 2) +
        (document.body.clientWidth > 1023 ? 200 : 500);

      setTimeout(
        () => {
          window.scroll({
            top:
              sectionHead.getBoundingClientRect().top +
              (window.scrollY || document.documentElement.scrollTop) -
              container.getBoundingClientRect().height,
            left: 0,
            behavior: "smooth",
          });
        },
        document.body.clientWidth > 1023 ? 0 : 500
      );

      setTimeout(() => {
        sectionHead.focus();
      }, Math.abs(focusTimeout));

      history.replaceState({}, "", el.getAttribute("href"));
    }
  };

  const onNavClick = (
    e:
      | React.MouseEvent<HTMLAnchorElement>
      | React.KeyboardEvent<HTMLAnchorElement>
  ) => {
    e.stopPropagation();

    if (device === "mobile") {
      setIsExpanded(false);
    }
    const hrefValue = (e.target as HTMLAnchorElement).getAttribute("href");

    if (hrefValue.substring(0, hrefValue.lastIndexOf("#")) === router.asPath) {
      const anchorId = hrefValue.substring(hrefValue.lastIndexOf("#"));

      if (anchorId.charAt(0) === "#") {
        e.preventDefault();
        scrollTo(e.target, anchorId);
      }
    }
  };

  const dropdownLabel = React.useMemo(() => {
    if (!navigationLinks || navigationLinks.length === 0) {
      return "";
    }

    // Find active link based on current router path
    const activeLink = navigationLinks.find(
      (link) => link?.fields?.url === router.asPath
    );

    if (activeLink) {
      return activeLink.fields.linkText || "";
    }

    // If no active link, fallback to first link text or dropdown link
    const firstLink = navigationLinks[0];
    return firstLink?.fields?.linkText || firstLink?.fields?.dropdownLink || "";
  }, [navigationLinks, router.asPath]);

  useEffect(() => {
    if (!navListRef.current) return;

    // Get all first-level focusable navigation elements
    const focusableElements: HTMLElement[] = [];
    const liItems = Array.from(navListRef.current.children);
    liItems.forEach((li) => {
      const link = li.querySelector<HTMLAnchorElement>("a");
      if (link) {
        focusableElements.push(link);
      }

      const button = li.querySelector<HTMLButtonElement>("button");
      if (button) {
        focusableElements.push(button);
      }
    });

    focusableElementsRef.current = focusableElements;
  }, [navigationLinks]);

  useEffect(() => {
    if (
      !stickComboRef.current ||
      !containerRef.current ||
      !stickComboWrapRef.current
    )
      return;

    // Make sure the content does not jump when the sticky state changes
    const updateMeasurements = () => {
      if (!stickComboRef.current) return;
      navOffsetTop.current =
        stickComboRef.current.getBoundingClientRect().top + window.scrollY;
      setNavHeight(stickComboWrapRef.current.offsetHeight);
    };

    updateMeasurements();

    // Toggle sticky state
    const onScroll = () => {
      // Fix component's display in Storybook
      if (window.scrollY === 0) {
        setIsSticky(false);
        return;
      }
      if (navOffsetTop.current === null) return;
      setIsSticky((prev) => {
        const isStickyNow = window.scrollY >= navOffsetTop.current;
        return prev === isStickyNow ? prev : isStickyNow;
      });
    };

    onScroll();

    const onResize = () => {
      updateMeasurements();
      onScroll();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [isSticky]);

  useEffect(() => {
    const nav = navigationRef.current;
    if (!nav) return;

    const els = nav.querySelectorAll<HTMLElement>(
      'a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])'
    );
    const last = els[els.length - 1];
    if (!last) return;

    const handle = (e: KeyboardEvent) => {
      if (e.key === "Tab" && !e.shiftKey) {
        setSubNavOpenId(null);
        setIsExpanded(false);
      }
    };

    last.addEventListener("keydown", handle);
    return () => last.removeEventListener("keydown", handle);
  }, [navigationLinks, isExpanded]);

  useEffect(() => {
    setIsMobile(device === "mobile" || device === "tablet");
  }, [device]);

  return (
    <div
      ref={containerRef}
      style={{ height: isSticky ? navHeight : "auto", position: "relative" }}
    >
      <div
        className={clsx(
          styles.component,
          styles["subnav-combo"],
          isSticky && styles["stick-combo"]
        )}
        ref={stickComboRef}
      >
        <div ref={stickComboWrapRef} className={styles["sticky-combo-wrap"]}>
          {isC722ProgrammeDetailsBarEMbaMerge && programmeDetailsBarDe ? (
            <C722_ProgrammeDetailsBarEMbaMerge
              isSticky={isSticky}
              content={programmeDetailsBarDe}
              programmeId={programmeId}
            />
          ) : (
            programmeDetailsBarDe && (
              <C721_ProgrammeDetailsDE
                isSticky={isSticky}
                content={programmeDetailsBarDe}
                programmeId={programmeId}
              />
            )
          )}
          <div
            ref={navigationRef}
            className={clsx(
              styles.component,
              styles["sub-navigation-two-level"],
              "blue",
              isExpanded && [styles.open, styles["sticky-combo-wrap--open"]],
              subNavOpenId?.length && styles["subnav-open"]
            )}
          >
            <div ref={stickyRef} className={styles["sticky-wrap"]}>
              <nav
                className={clsx(
                  "wrapper",
                  styles.wrapper,
                  styles["nav-wrapper"],
                  isExpanded && styles["wrapper--expanded"]
                )}
                aria-label="Sub Navigation"
              >
                <button
                  onClick={() => setIsExpanded((isExpanded) => !isExpanded)}
                  className={clsx(styles["nav__toggle"], "md:hidden")}
                  aria-expanded={isExpanded}
                  aria-haspopup
                  aria-controls={navListId}
                >
                  {dropdownLabel}
                  <Arrow aria-hidden className={styles["icon-chevron"]} />
                </button>
                <ul
                  ref={navListRef}
                  className={clsx(styles["subnav__list"], "subnav__list")}
                  id={navListId}
                  hidden={!isExpanded && isMobile}
                >
                  {navigationLinks?.map((link, i) => (
                    <React.Fragment key={`${i}${link.fields?.linkText}`}>
                      {link.fields.linkText ? (
                        <li className={styles["subnav__item"]}>
                          <NavLink
                            {...link.fields}
                            onNavClick={onNavClick}
                            isActive={router.asPath === link.fields.url}
                            onNavKeyDown={handleFirstLevelKeyDown}
                          />
                        </li>
                      ) : (
                        <NavDropdown
                          id={`${i}${link.fields.dropdownLink}`}
                          isOpen={
                            subNavOpenId === `${i}${link.fields.dropdownLink}`
                          }
                          activeURL={router.asPath}
                          dropdownLinkHref={link.fields.dropdownLinkHref}
                          dropdownLinkText={link.fields.dropdownLink}
                          secondaryNavLinks={link.fields.secondaryNavLinks}
                          onNavClick={onNavClick}
                          onToggle={handleSubmenuToggle}
                          onFirstLevelKeyDown={handleFirstLevelKeyDown}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </ul>
                <NavCtas
                  ctaButton1={ctaButton1}
                  ctaLink1={ctaLink1}
                  ctaButton1Text={ctaButton1Text}
                  onClickHandler={() => {
                    setShowLightbox(true);
                  }}
                  applyNowUrl={
                    useApplicationLink({ user: !!user, programmeApplyOnlineId, isSalesforceApplication })
                  }
                />
              </nav>
            </div>
          </div>
        </div>
      </div>
      {showLightbox && (
        <ModalEMBAMergeForm
          locations={enableEmba2027intakeOptions
            ? modalOptions
            : legacyModalLocations}
          formHeader={formHeaderEmbaMerge}
          buttonText={buttonTextEmbaMerge}
          errorMessage={errorMessageEmbaMerge}
          redirectDelay={500}
          onCloseFunction={closeLightbox}
          user={user}
        />
      )}
    </div>
  );
};

export default SubNavigationDE;
